import { ReactElement } from "react";

import { HStack, Pressable, Text } from "@/components/ui";

/* Small controls shared by the tracker and its sheets. */

interface SegmentedProps<T extends string | number> {
  onChange: (value: T) => void;
  options: { label: string; value: T }[];
  value: T;
}

/** Segmented control styled like the launch simulator tabs. */
export const Segmented = <T extends string | number>({
  onChange,
  options,
  value,
}: SegmentedProps<T>): ReactElement => (
  <HStack
    className="self-start p-1 rounded-lg flex-wrap"
    style={{ backgroundColor: "hsl(220,13%,91%)" }}
  >
    {options.map((option) => {
      const active = option.value === value;
      return (
        <Pressable
          className={`px-3 py-1.5 rounded-md ${active ? "bg-background-0 shadow-soft-1" : ""}`}
          key={String(option.value)}
          onPress={() => onChange(option.value)}
        >
          <Text
            className={`text-sm font-medium ${active ? "text-typography-900" : "text-typography-500"}`}
          >
            {option.label}
          </Text>
        </Pressable>
      );
    })}
  </HStack>
);

interface ChipProps {
  active: boolean;
  label: string;
  onPress: () => void;
}

/** Toggle chip for multi-select lists (offices, states). */
export const Chip = ({ active, label, onPress }: ChipProps): ReactElement => (
  <Pressable
    className={`px-2.5 py-1 rounded-full border ${active ? "border-primary-500 bg-primary-50" : "border-outline-200 bg-background-0"}`}
    onPress={onPress}
  >
    <Text
      className={`text-xs font-medium ${active ? "text-primary-700" : "text-typography-500"}`}
    >
      {label}
    </Text>
  </Pressable>
);

/** Uppercase field label used above controls. */
export const FieldLabel = ({ label }: { label: string }): ReactElement => (
  <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
    {label}
  </Text>
);
